import { Component, OnInit } from '@angular/core';
import { ServiceBusService } from '../system/service-bus.service';
import { Chanels } from '../chanels';
import { SiteService } from '../entity/site.service';
import { Site, SiteFiles } from '../entity/site';
import { AttachmentFile } from '../react-components/FileUpload/attachment';
import { extractExtension, isValidExtension } from '../react-components/FileUpload/FileUpload';
import { Guid } from '../system/guid';
import { hasAdminRole, hasSupervisortRole } from '../auth-utils';
import i18next from 'i18next';
import { useImageUpload, FILEUPLOAD_URL } from '../checklist-react/services/checklistService';
import tippy from 'tippy.js';

@Component({
  selector: 'app-file-attachments',
  templateUrl: './file-attachments.component.html',
  styleUrls: ['./file-attachments.component.scss']
})
export class FileAttachmentsComponent implements OnInit {
  public siteId: string;
  public files: SiteFiles[] = [];
  public isLoadingFiles = false;
  private site: Site;
  public isSupervisorUser: boolean;
  private user: any;

  constructor(
    private bus: ServiceBusService,
    private siteService: SiteService
  ) { }

  ngOnInit() {
    this.bus.subscribe(Chanels.USER_CHANEL, (user) => {
      if (user == null) return;

      this.user = user;
      this.siteId = user.siteId;
      this.isSupervisorUser = hasAdminRole(user?.adalUser?.roles);
      
      this.loadSiteFiles();
    });

    // Initialize tooltips
    tippy('#attachment-info', {
      content: `<div style="background-color: rgba(33, 29, 29, 0.9); color: white; padding: 12px; border-radius: 10px;">
        <div>${i18next.t('dashboard.tooltips.attachmentStatus')}</div>
      </div>`,
      allowHTML: true,
    });
  }

  private loadSiteFiles() {
    this.isLoadingFiles = true;
    this.siteService.getSite(this.siteId).subscribe(
      (site) => {
        this.site = site;
        this.files = site.files?.map(file => {
          return {
            id: file.id,
            name: file.name,
            fileUrl: file.fileUrl,
            type: file.type,
            size: file.size
          };
        }) || [];
        
        this.isLoadingFiles = false;
      },
      (error) => {
        console.error('Error loading site files:', error);
        this.isLoadingFiles = false;
      }
    );
  }

  async onFileSelected(event: any) {
    const file = event.target.files[0]; 
    if (!file) return;

    if (!isValidExtension(file.name)) {
      alert(i18next.t('fileAttachments.unsupportedFileFormat'));
      return;
    }

    const fileExists = this.site.files?.some(existingFile => existingFile.name === file.name);
    if (fileExists) {
      alert(i18next.t('fileAttachments.fileAlreadyExists'));
      return;
    }

    const attachment = AttachmentFile({});
    attachment.append(file);

    try {
      const fileId = Guid.newGuid().toString();
      const extension = extractExtension(file.name);
      const fileUrl = `site/${fileId}${extension}`;

      const img = AttachmentFile(attachment);
      if (img.hasAttachments) {
        try {
          await useImageUpload(
            img.dataFor(file),
            fileId,
            extension,
            'site'
          );

          if (!this.site.files) {
            this.site.files = [];
          }
          
          const newFile = {
            id: fileId,
            name: file.name, 
            fileUrl: fileUrl,
            type: extension,
            size: file.size
          };
          this.site.files.push(newFile);
          
          await this.siteService.putSite(this.site.id, this.site).toPromise();
          
          this.loadSiteFiles();
          
        } catch (uploadError) {
          console.error('Error during file upload to Azure:', uploadError?.response?.data || uploadError);
          throw uploadError;
        }
      }
    } catch (err) {
      console.error('Error in file handling:', err);
    }
  }

  async deleteFile(fileName: string) {
    if (!this.isSupervisorUser) {
      alert(i18next.t('fileAttachments.userNotAuthorized'));
      return;
    }

    if (!confirm(i18next.t('fileAttachments.deleteFileConfirmation'))) return;
    try {
      const fileToDelete = this.site.files.find(file => file.name === fileName);
      if (!fileToDelete) {
        console.error('File not found:', fileName);
        return;
      }
        
      this.site.files = this.site.files.filter(f => f.name !== fileName);
      await this.siteService.putSite(this.site.id, this.site).toPromise();
      
      this.loadSiteFiles();
    } catch (error) {
      console.error('Error updating site in database:', error);
      throw error;
    }
  }
  
  getFileDownloadUrl(fileUrl: string): string {
    return `${FILEUPLOAD_URL}/${fileUrl}`;
  }
}